import { gql } from "@apollo/client/core";

// Must match EventService.DEFAULT_EVENT_CONTRACT_CODE in ITS.Events.
export const EVENT_CONTRACT_CODE = "ow-event";

const SUCCESS_RESPONSE_FIELDS = gql`
  fragment EventsSuccessResponseFields on SuccessResponse {
    success
    message
  }
`;

export const GET_LIVE_CONTRACT = gql`
  query EventsGetLiveContract($code: String!) {
    getLiveContract(code: $code) {
      contractId
      content
      metadataSchema
    }
  }
`;

export const LIST_RECOVERABLE_EVENTS = gql`
  query EventsListEvents(
    $organizationId: String!
    $organizerUserId: String
    $q: String
    $limit: Int
  ) {
    listEvents(
      organizationId: $organizationId
      organizerUserId: $organizerUserId
      q: $q
      limit: $limit
    ) {
      eventId
      eventName
      destination
      startDate
      endDate
      status
      createdAt
    }
  }
`;

export const IS_EVENT_NAME_AVAILABLE = gql`
  query EventsIsEventNameAvailable(
    $eventName: String!
    $organizationId: String!
  ) {
    isEventNameAvailable(eventName: $eventName, organizationId: $organizationId)
  }
`;

export const LIST_ADMIN_EVENTS_PAGE_INDEX = gql`
  query EventsListEventsPageIndex(
    $organizationId: String!
    $state: String
    $q: String
    $limit: Int
    $nextToken: String
  ) {
    listEventsPage(
      organizationId: $organizationId
      state: $state
      q: $q
      limit: $limit
      nextToken: $nextToken
    ) {
      nextToken
      items {
        eventId
        eventName
        eventType
        destination
        organizerEmail
        startDate
        endDate
        status
        updatedAt
      }
    }
  }
`;

export const LIST_ORGANIZER_EVENTS_PAGE_INDEX = gql`
  query EventsListEventsPageIndex(
    $organizationId: String!
    $organizerUserId: String
    $state: String
    $q: String
    $limit: Int
    $nextToken: String
  ) {
    listEventsPage(
      organizationId: $organizationId
      organizerUserId: $organizerUserId
      state: $state
      q: $q
      limit: $limit
      nextToken: $nextToken
    ) {
      nextToken
      items {
        eventId
        eventName
        estimatedTravelerCount
        destination
        startDate
        endDate
        status
      }
    }
  }
`;

export const GET_ADMIN_EVENT_ROLLUPS = gql`
  query EventsGetAdminEventRollups($organizationId: String) {
    getAdminEventRollups(organizationId: $organizationId) {
      review
      active
      total
      byStatus
    }
  }
`;

export const GET_ADMIN_EVENT = gql`
  query EventsGetEvent($eventId: ID!, $organizationId: String!) {
    getEvent(eventId: $eventId, organizationId: $organizationId) {
      eventId
      eventName
      eventType
      description
      destination
      venue
      venueAddress
      startDate
      endDate
      estimatedTravelerCount
      status
      organizerEmail
      createdAt
      updatedAt
    }
  }
`;

export const GET_ORGANIZER_EVENT = gql`
  query EventsGetEvent($eventId: ID!, $organizationId: String!) {
    getEvent(eventId: $eventId, organizationId: $organizationId) {
      eventId
      eventName
      destination
      venue
      venueAddress
      startDate
      endDate
      origin
      status
    }
  }
`;

export const GET_INVITATION_EVENT = gql`
  query EventsGetEvent($eventId: ID!, $organizationId: String!) {
    getEvent(eventId: $eventId, organizationId: $organizationId) {
      eventId
      eventName
      description
      destination
      venue
      venueAddress
      startDate
      endDate
      status
    }
  }
`;

export const GET_EVENT_REJECTION = gql`
  query EventsGetEventRejection($eventId: ID!, $organizationId: String!) {
    getEvent(eventId: $eventId, organizationId: $organizationId) {
      rejectReason
    }
  }
`;

export const GET_EVENT_LANDING_PAGE = gql`
  query GetEventLandingPage($eventId: ID!) {
    getEventLandingPage(eventId: $eventId) {
      slug
      title
      subtitle
      description
      registrationOpen
      links {
        oneWorldBookingLinks {
          links {
            currency
            eventLink
          }
        }
      }
    }
  }
`;

export const GET_ADMIN_EVENT_LANDING_PAGE = gql`
  query GetAdminEventLandingPage($eventId: ID!) {
    getEventLandingPage(eventId: $eventId) {
      slug
      title
      subtitle
      description
    }
  }
`;

export const UPSERT_EVENT_LANDING_PAGE = gql`
  mutation UpsertEventLandingPage(
    $eventId: ID!
    $input: UpsertLandingPageInput!
  ) {
    upsertEventLandingPage(eventId: $eventId, input: $input) {
      slug
      title
      subtitle
      description
      registrationOpen
    }
  }
`;

export const CREATE_EVENT = gql`
  mutation EventsCreateEvent(
    $input: CreateEventInput!
    $organizerName: String!
  ) {
    createEvent(input: $input, organizerName: $organizerName) {
      eventId
    }
  }
`;

export const UPDATE_EVENT = gql`
  mutation EventsUpdateEvent($eventId: ID!, $input: UpdateEventInput!) {
    updateEvent(eventId: $eventId, input: $input) {
      eventId
      eventName
      description
      venue
      venueAddress
      startDate
      endDate
    }
  }
`;

export const DELETE_EVENT = gql`
  mutation EventsDeleteEvent($eventId: ID!, $organizationId: String!) {
    deleteEvent(eventId: $eventId, organizationId: $organizationId) {
      ...EventsSuccessResponseFields
    }
  }
  ${SUCCESS_RESPONSE_FIELDS}
`;

export const PUBLISH_EVENT = gql`
  mutation EventsPublishEvent($eventId: ID!, $organizationId: String!) {
    publishEvent(eventId: $eventId, organizationId: $organizationId) {
      eventId
    }
  }
`;

export const CANCEL_EVENT = gql`
  mutation EventsCancelEvent($eventId: ID!, $reason: String) {
    cancelEvent(eventId: $eventId, reason: $reason) {
      eventId
    }
  }
`;

export const DECLINE_EVENT_REVIEW = gql`
  mutation DeclineEventReview($eventId: ID!, $reason: String) {
    declineEventReview(eventId: $eventId, reason: $reason) {
      eventId
    }
  }
`;

export const ARCHIVE_EVENT = gql`
  mutation EventsArchiveEvent($eventId: ID!) {
    archiveEvent(eventId: $eventId) {
      eventId
    }
  }
`;

export const COMPLETE_EVENT = gql`
  mutation EventsCompleteEvent($eventId: ID!) {
    completeEvent(eventId: $eventId) {
      eventId
    }
  }
`;

export const OPEN_EVENT_REGISTRATION = gql`
  mutation EventsOpenEventRegistration($eventId: ID!) {
    openEventRegistration(eventId: $eventId) {
      eventId
    }
  }
`;

export const ACTIVATE_EVENT = gql`
  mutation ActivateEvent(
    $input: ActivateEventInput!
    $organizationId: String!
  ) {
    activateEvent(input: $input, organizationId: $organizationId) {
      eventId
    }
  }
`;

export const APPROVE_EVENT_REVIEW = gql`
  mutation ApproveEventReview(
    $input: ActivateEventInput!
    $organizationId: String!
  ) {
    approveEventReview(input: $input, organizationId: $organizationId) {
      eventId
    }
  }
`;

const EVENT_AGREEMENT_FIELDS = gql`
  fragment EventsEventAgreementFields on EventAgreement {
    signedContractId
    status
    acceptedAt
  }
`;

export const PREVIEW_EVENT_AGREEMENT = gql`
  mutation PreviewEventAgreement($contractId: ID!, $values: AWSJSON!) {
    previewEventAgreement(contractId: $contractId, values: $values) {
      contractId
      values
      content
      renderVersion
      acceptedAt
      previewHash
    }
  }
`;

export const SIGN_EVENT_AGREEMENT = gql`
  mutation SignEventAgreement(
    $eventId: ID!
    $organizationId: String!
    $requestId: String!
    $typedSignature: String!
    $previewHash: String!
    $previewAcceptedAt: String!
    $previewRenderVersion: String!
    $values: AWSJSON!
  ) {
    signEventAgreement(
      eventId: $eventId
      organizationId: $organizationId
      requestId: $requestId
      typedSignature: $typedSignature
      previewHash: $previewHash
      previewAcceptedAt: $previewAcceptedAt
      previewRenderVersion: $previewRenderVersion
      values: $values
    ) {
      signedContractId
      status
    }
  }
`;

export const GET_EVENT_AGREEMENT = gql`
  query GetEventAgreement($eventId: ID!, $organizationId: String!) {
    getEventAgreement(eventId: $eventId, organizationId: $organizationId) {
      ...EventsEventAgreementFields
    }
  }
  ${EVENT_AGREEMENT_FIELDS}
`;

export const DOWNLOAD_EVENT_CONTRACT_PDF = gql`
  mutation DownloadEventContractPdf($eventId: ID!, $organizationId: String!) {
    downloadEventContractPdf(
      eventId: $eventId
      organizationId: $organizationId
    ) {
      contentBase64
      contentType
      filename
    }
  }
`;

export const GET_EVENT_BOOKING_MANIFEST = gql`
  query GetEventBookingManifest($eventId: ID!, $cursor: String, $limit: Int) {
    getEventBookingManifest(eventId: $eventId, cursor: $cursor, limit: $limit) {
      count
      cursor
      snapshotAt
      items {
        bookingManifestId
        pnr
        manifestPassengerId
        ticketNumber
        passengerName
        flightNumber
        bookingStatus
        currency
        amountPaid
        snapshotAt
        updatedAt
      }
    }
  }
`;

export const GET_EVENT_DASHBOARD = gql`
  query EventsGetEventDashboard($eventId: ID!) {
    getEventDashboard(eventId: $eventId) {
      stats {
        travelCounts {
          booked
        }
      }
    }
  }
`;

export const GET_CALENDAR_BY_EVENT_ID = gql`
  query EventsGetCalendarByEventId($eventId: ID!) {
    getCalendarByEventId(eventId: $eventId) {
      organizerDisplayName
      organizerEmail
    }
  }
`;
